import type { Distance } from "@pdfking/math"
import { Max, Min } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import { CM, MM } from "@pdfking/quantity"

import type { BoxPointList, BoxPointLists, GrowPlotter, PlotContribute } from "#internal"
import { CUT_LAYER_ID, FOLD_LAYER_ID } from "#internal"

import { ExtractVariables } from "./ExtractVariables"
import { MirrorX } from "./MirrorX"
import { PlotPointsList } from "./PlotPointsList"
import { PointAccumulate } from "./PointAccumulate"
import { TranslatePoints } from "./TranslatePoints"

const MIN_PACKAGING_SHEET_SIZE = MM(1)
const FLAP_MOLT = 0.25 // 1 / 4
const MAX_FLAP = CM(10)
const CORNER_OFFSET = MM(5)

const accumulate = (values: Distance[]): Distance[] => {
  let total = ZERO_DISTANCE
  return values.map(v => {
    total = (total + v) as Distance
    return total
  })
}

const flap_size = (width: Distance, height: Distance, depth: Distance): Distance =>
  Min(Max(MAX_FLAP, depth), width * FLAP_MOLT, height * FLAP_MOLT) as Distance

const merge = (items: BoxPointLists[]): BoxPointLists => items.flat()

const PlotPackagingImpl =
  (has_opening: boolean): GrowPlotter =>
  size => {
    const { w, h, d, s } = ExtractVariables(size, { min_s: MIN_PACKAGING_SHEET_SIZE })
    const fs = flap_size(w, h, d)
    const ts = (fs + d + s * 4) as Distance
    const hmod = ts
    const wmod = has_opening ? ((w + d + s * 2) as Distance) : ts

    const cut_parts: BoxPointLists[] = [
      PointAccumulate([
        { x: ts + w, y: ts + h }, // x: ts * 2 + w - ts
        { x: wmod, y: 0 },
        { x: 0, y: -h },
        { x: -wmod, y: 0 },
        { x: 0, y: -ts },
        { x: -w, y: 0 },
        { x: 0, y: ts },
        { x: -ts, y: 0 },
        { x: 0, y: h },
        { x: ts, y: 0 },
        { x: 0, y: hmod },
        { x: w, y: 0 },
      ] as BoxPointList),
    ]

    const fold_parts: BoxPointLists[] = [
      PointAccumulate(
        ...accumulate([fs, s, (d + 2 * s) as Distance, s, w, s, (d + 2 * s) as Distance, s]).map(
          dist =>
            [
              { x: dist, y: ts },
              { x: 0, y: h },
            ] as BoxPointList
        ),
        ...accumulate([fs, s, (d + 2 * s) as Distance, s, h, s, (d + 2 * s) as Distance, s]).map(
          dist =>
            [
              { x: ts, y: dist },
              { x: w, y: 0 },
            ] as BoxPointList
        )
      ),
    ]

    const cs = (ts - CORNER_OFFSET) as Distance
    const cf = ((cs - d - s * 2) / 2) as Distance

    const cut = PointAccumulate(
      [
        { x: 0, y: 0 },
        { x: 0, y: cs },
        { x: cs / 2, y: 0 },
        { x: 0, y: -CORNER_OFFSET },
        { x: CORNER_OFFSET + s, y: 0 },
        { x: 0, y: 2 * CORNER_OFFSET - cf },
        { x: -(CORNER_OFFSET + s), y: 0 },
        { x: 0, y: -(CORNER_OFFSET + s) },
        { x: 0, y: CORNER_OFFSET + s },
        { x: cs / 2, y: 0 },
        { x: 0, y: -(s * 2 + d + CORNER_OFFSET * 2) },
        { x: -cs / 2, y: 0 },
        { x: 0, y: s + CORNER_OFFSET },
        { x: 0, y: -(s + CORNER_OFFSET) },
        { x: s + CORNER_OFFSET, y: 0 },
        { x: 0, y: 2 * CORNER_OFFSET - cf },
        { x: -(CORNER_OFFSET + s), y: 0 },
        { x: 0, y: -CORNER_OFFSET },
      ] as BoxPointList,
      ...[cf, (cf + s + d) as Distance].map(
        y =>
          [
            { x: cs / 2 + CORNER_OFFSET, y },
            { x: cf - CORNER_OFFSET * 2, y: 0 },
            { x: 0, y: s },
            { x: 2 * CORNER_OFFSET - cf, y: 0 },
          ] as BoxPointList
      )
    )

    const fold = PointAccumulate(
      ...[
        cf,
        (cf + s) as Distance,
        (cf + s + d) as Distance,
        (cf + 2 * s + d) as Distance /* cf + s + d + s */,
      ].map(
        y =>
          [
            { x: 0, y },
            { x: cs / 2, y: 0 },
          ] as BoxPointList
      ),
      [
        { x: cs / 2, y: cf + s },
        { x: 0, y: d },
      ] as BoxPointList
    )

    for (const [raw_x, offset_y, mirrored] of [
      [-CORNER_OFFSET as Distance, ZERO_DISTANCE, true],
      [(ts + w + CORNER_OFFSET) as Distance, ZERO_DISTANCE, false],
      [-CORNER_OFFSET as Distance, (ts + h + CORNER_OFFSET) as Distance, true],
      [(ts + w + CORNER_OFFSET) as Distance, (ts + h + CORNER_OFFSET) as Distance, false],
    ] as const) {
      const offset_x = mirrored ? ((raw_x - ts) as Distance) : raw_x
      cut_parts.push(TranslatePoints(offset_x, offset_y, mirrored ? MirrorX(cut, ts) : cut))
      fold_parts.push(TranslatePoints(offset_x, offset_y, mirrored ? MirrorX(fold, ts) : fold))
    }

    return {
      [CUT_LAYER_ID]: [...PlotPointsList(merge(cut_parts), "cut", { closed: true })],
      [FOLD_LAYER_ID]: [...PlotPointsList(merge(fold_parts), "fold")],
    } as PlotContribute
  }

export const PlotPackaging: GrowPlotter = PlotPackagingImpl(false)
export const PlotPackagingAlt: GrowPlotter = PlotPackagingImpl(true)

export const PackagingGrow = (
  side: Distance,
  other: Distance,
  depth: Distance = ZERO_DISTANCE,
  sheet_depth: Distance = ZERO_DISTANCE
): Distance =>
  (side +
    flap_size(side, other, depth) * 2 +
    2 * depth +
    8 * Max(sheet_depth, MIN_PACKAGING_SHEET_SIZE)) as Distance

export const PackagingOpening = (
  width: Distance,
  height: Distance,
  depth: Distance = ZERO_DISTANCE,
  sheet_depth: Distance = ZERO_DISTANCE
): Distance =>
  (width * 2 +
    flap_size(width, height, depth) +
    2 * depth +
    8 * Max(sheet_depth, MIN_PACKAGING_SHEET_SIZE)) as Distance
